import React, { useMemo, useRef, useEffect, useState, useContext } from "react";
import { Box, Typography, IconButton, Stack, Button, TextField, InputAdornment, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Close as CloseIcon, Search as SearchIcon, ArrowBack, ChatBubbleOutline as ChatIcon, CallOutlined as CallIcon, OpenInFull as OpenInFullIcon, CloseFullscreen as CloseFullscreenIcon } from "@mui/icons-material";
import { IoMdVolumeHigh, IoMdVolumeOff } from "react-icons/io";
import { WidgetContext } from "../../store/widgetContext";
import { toggleFullscreen } from "../../routes/host-bridge";
import AvatarStack from "../AvatarStack";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { get } from "lodash";

export default function Header({ widgetState = {}, handlers = {}, searchQuery, onSearchChange, widgetHeight, children, selectedDocument, selectedCollection, chatMode, onChatModeChange, isFullscreen: isFullscreenProp = false, isSmallScreen = false }) {
	const { widget, setCollapsed, isMuted, setIsMuted } = useContext(WidgetContext);
	const location = widgetState.location || "home";
	const { isVoiceChatEnabled, isTextChatEnabled } = widgetState;
	const { handleNavigate, goBack, setSelectedCollection } = handlers;
	const defaultCompany = get(widget, "chat.header.company", "");
	const defaultMessage = get(widget, "chat.header.title", "");
	const headerIcon = get(widget, "chat.header.icon", "");
	const headerBgColor = get(widget, "chat.header.backgroundColor", "#000000");
	const headerSecondaryColor = get(widget, "chat.header.secondaryColor", "#666666");
	const headerTitleColor = get(widget, "chat.header.titleColor", "#ffffff");
	const coolMode = get(widget, "chat.header.coolMode", false);
	const { msg } = useWidgetMsg();
	
	const isHomePage = location === "home" || !location;
	const contentRef = useRef(null);
	const [height, setHeight] = useState(100);
	const [isFullscreen, setIsFullscreen] = useState(isFullscreenProp);

	const headerBackground = useMemo(() => {
		if (coolMode && headerSecondaryColor) {
			return `linear-gradient(180deg, ${headerBgColor} 0%, ${headerSecondaryColor} 100%)`;
		}
		return headerBgColor;
	}, [coolMode, headerBgColor, headerSecondaryColor]);

	const pageTitle = useMemo(() => {
		if (isHomePage) return null;
		switch (location) {
			case "conversations":
				return msg("conversations.title", "Conversations");
			case "tickets":
				return msg("tickets.title", "Tickets");
			case "articles":
				return msg("help.title", "Help");
			default:
				return null;
		}
	}, [isHomePage, location, msg]);

	const handleCloseClick = (event) => {
		if (event) {
			event.stopPropagation();
		}
		setCollapsed(true);
	};

	const toggleMute = () => setIsMuted((prev) => !prev);
	const handleToggleFullscreen = () => toggleFullscreen();

	const handleBackClick = () => {
		if (!goBack) return;

		if (location === "document") {
			if (widgetState.previousLocation) {
				goBack();
			} else if (selectedDocument?.previousCollection) {
				setSelectedCollection(selectedDocument.previousCollection);
				handleNavigate({ location: "collection" });
			} else {
				setSelectedCollection(null);
				handleNavigate({ location: "articles" });
			}
		} else if (location === "collection") {
			if (selectedCollection?.parentCollection) {
				setSelectedCollection(selectedCollection.parentCollection);
				handleNavigate({ location: "collection" });
			} else {
				setSelectedCollection(null);
				handleNavigate({ location: "articles" });
			}
		} else if (location === "newConversation" || location === "newVoiceChat") {
			goBack();
		}
	};

	useEffect(() => setIsFullscreen(isFullscreenProp), [isFullscreenProp]);

	useEffect(() => {
		if (contentRef.current) {
			const measuredHeight = contentRef.current.scrollHeight;
			const hasComplexContent = location === "tickets" || location === "articles" || location === "collection" || location === "conversations";
			const extraHeight = location === "document" && selectedDocument?.description ? 30 : 0;
			const maxHeight = !hasComplexContent && !isHomePage && widgetHeight ? Math.min(150, widgetHeight * 0.25) : undefined;
			setHeight((maxHeight ? Math.min(maxHeight, measuredHeight + 40) : measuredHeight + 40) + extraHeight);
		}
	}, [location, pageTitle, isHomePage, isVoiceChatEnabled, isTextChatEnabled, selectedDocument, selectedCollection, defaultMessage, widgetHeight, widgetState.assignedAgents]);

	return (
		<Box sx={{ padding: "10px", paddingBottom: "10px", boxSizing: "border-box" }}>
			<Box
				sx={{
					width: "100%",
					maxWidth: "100%",
					height: `${height}px`,
					background: headerBackground,
					borderRadius: "16px",
					position: "relative",
					padding: "20px",
					boxSizing: "border-box",
					display: "flex",
					flexDirection: "column",
					justifyContent: isHomePage ? "space-between" : "flex-start",
					gap: isHomePage ? 0 : 2,
					transition: "height 0.3s ease-in-out, gap 0.3s ease-in-out",
					overflow: "hidden",
				}}
			>
				<Box ref={contentRef} sx={{ display: "flex", flexDirection: "column", gap: isHomePage ? 0 : 2, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
					{(location === "newConversation" || location === "newVoiceChat") ? (
						<Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", maxWidth: "100%" }}>
							<IconButton size="small" onClick={handleBackClick} sx={{ color: headerTitleColor, padding: "4px" }}>
								<ArrowBack fontSize="small" />
							</IconButton>
							<Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", "& .agent-name": { color: headerTitleColor }, "& .agent-presence": { color: headerTitleColor, opacity: 0.8 } }}>
								<AvatarStack agents={widgetState.assignedAgents || []} displayAgentInfo={true} />
							</Box>
							<IconButton size="small" sx={{ color: headerTitleColor, padding: "4px", marginLeft: "0px !important"  }} onClick={toggleMute}>
								{isMuted ? <IoMdVolumeOff style={{ fontSize: 20 }} /> : <IoMdVolumeHigh style={{ fontSize: 20 }} />}
							</IconButton>
							<IconButton size="small" sx={{ color: headerTitleColor, padding: "4px", marginLeft: "0px !important" }} onClick={handleCloseClick}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Stack>
					) : location === "document" ? (
						<>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", maxWidth: "100%" }}>
								<Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
									<IconButton size="small" onClick={handleBackClick} sx={{ color: headerTitleColor, padding: "4px", mr: 1 }}>
										<ArrowBack fontSize="small" />
									</IconButton>
									{headerIcon && (
										<Box component="img" src={headerIcon} alt="Logo" sx={{ width: 24, height: 24, objectFit: "contain" }} />
									)}
									<Typography variant="body1" sx={{ color: headerTitleColor, fontSize: "16px", fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										{defaultCompany}
									</Typography>
								</Stack>
								{!isSmallScreen && (
									<IconButton size="small" sx={{ color: headerTitleColor, padding: "4px" }} onClick={handleToggleFullscreen}>
										{isFullscreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
									</IconButton>
								)}
								<IconButton size="small" sx={{ color: headerTitleColor, padding: "4px" }} onClick={handleCloseClick}>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Stack>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
								{selectedDocument?.title && (
									<Typography
										variant="h5"
										title={selectedDocument.title}
										sx={{
											color: headerTitleColor,
											fontSize: "24px",
											fontWeight: 400,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											width: "100%",
										}}
									>
										{selectedDocument.title}
									</Typography>
								)}
								{selectedDocument?.description && (
									<Typography
										variant="body1"
										title={selectedDocument.description}
										sx={{
											color: headerTitleColor,
											fontSize: "14px",
											fontWeight: 400,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											width: "100%",
										}}
									>
										{selectedDocument.description}
									</Typography>
								)}
							</Box>
						</>
					) : location === "collection" ? (
						<>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", maxWidth: "100%" }}>
								<Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
									<IconButton size="small" onClick={handleBackClick} sx={{ color: headerTitleColor, padding: "4px", mr: 1 }}>
										<ArrowBack fontSize="small" />
									</IconButton>
									{headerIcon && (
										<Box component="img" src={headerIcon} alt="Logo" sx={{ width: 24, height: 24, objectFit: "contain" }} />
									)}
									<Typography variant="body1" sx={{ color: headerTitleColor, fontSize: "16px", fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										{defaultCompany}
									</Typography>
								</Stack>
								<IconButton size="small" sx={{ color: headerTitleColor, padding: "4px" }} onClick={handleCloseClick}>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Stack>
							{selectedCollection?.title && (
								<Typography
									variant="h5"
									sx={{
										color: headerTitleColor,
										fontSize: "24px",
										fontWeight: 400,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										width: "100%",
									}}
								>
									{selectedCollection.title}
								</Typography>
							)}
						</>
					) : (
						<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%", maxWidth: "100%" }}>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
								{headerIcon && (
									<Box component="img" src={headerIcon} alt="Logo" sx={{ width: 24, height: 24, objectFit: "contain" }} />
								)}
								<Typography variant="body1" sx={{ color: headerTitleColor, fontSize: "16px", fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{defaultCompany}
								</Typography>
							</Stack>
							{(location === "home" || location === "conversations") && (
								<IconButton size="small" sx={{ color: headerTitleColor, padding: "4px" }} onClick={toggleMute}>
									{isMuted ? <IoMdVolumeOff style={{ fontSize: 20 }} /> : <IoMdVolumeHigh style={{ fontSize: 20 }} />}
								</IconButton>
							)}
							<IconButton size="small" sx={{ color: headerTitleColor, padding: "4px" }} onClick={handleCloseClick}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Stack>
					)}

					{isHomePage && defaultMessage && (
						<Typography variant="h6" sx={{ color: headerTitleColor, fontSize: "20px", fontWeight: 300, lineHeight: 1.4, mt: 2 }}>
							{defaultMessage}
						</Typography>
					)}

					{pageTitle && (
						<Typography variant="h5" sx={{ color: headerTitleColor, fontSize: "24px", fontWeight: 400 }}>
							{pageTitle}
						</Typography>
					)}

             		{location === "conversations" && isTextChatEnabled && isVoiceChatEnabled && (
						<ToggleButtonGroup
							value={chatMode}
							exclusive
							onChange={(_, newMode) => {
								if (newMode !== null && onChatModeChange) {
									onChatModeChange(newMode);
								}
							}}
							sx={{
								width: "100%",
								gap: "8px",
								"& .MuiToggleButton-root": {
									border: `1px solid white`,
									borderRadius: "30px",
									textTransform: "uppercase",
									fontSize: "12px",
									padding: "8px 16px",
									backgroundColor: "transparent",
									color: "white",
									flex: 1,
									"&.Mui-selected": {
										background: "white !important",
										color: "black !important",
									},
								},
							}}
						>
							{isTextChatEnabled && (
								<ToggleButton value="chat" sx={{ borderRadius: "30px", display: "flex", alignItems: "center", gap: "8px" }}>
									<ChatIcon sx={{ fontSize: 16 }} />
									{msg("conversations.chats", "Chats")}
								</ToggleButton>
							)}
							{isVoiceChatEnabled && (
								<ToggleButton value="voice" sx={{ borderRadius: "30px", display: "flex", alignItems: "center", gap: "8px" }}>
									<CallIcon sx={{ fontSize: 16 }} />
									{msg("conversations.calls", "Calls")}
								</ToggleButton>
							)}
						</ToggleButtonGroup>
					)}
					{(location === "tickets" || location === "articles" || location === "collection") && (
						<TextField
							fullWidth
							placeholder={msg("home.search", "Search")}
							value={searchQuery || ""}
							onChange={onSearchChange}
							size="small"
							sx={{
								width: "100%", maxWidth: "100%",
								"& .MuiOutlinedInput-root": {
									background: "rgba(255, 255, 255, 0.2)",
									borderRadius: "8px",
									"& fieldset": { border: "1px solid rgba(255, 255, 255, 0.3)" },
									"&:hover fieldset": { border: "1px solid rgba(255, 255, 255, 0.5)" },
									"&.Mui-focused fieldset": { border: "1px solid rgba(255, 255, 255, 0.7)" },
									"& input": { color: headerTitleColor, "&::placeholder": { color: headerTitleColor, opacity: 1 } },
								},
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon sx={{ color: headerTitleColor, fontSize: 16 }} />
									</InputAdornment>
								),
							}}
						/>
					)}
					{children}
				</Box>
			</Box>
		</Box>
	);
}

